import type {
  GameState,
  GamePhase,
  GameEvent,
  GameEventObserver,
  GameConfig,
  PlayerId,
  WarriorInstance,
  WarriorDefinition,
  BoardPosition,
  AIDifficulty,
} from '@/types';
import { BoardManager } from './BoardManager';
import { TurnManager } from './TurnManager';
import { CombatResolver } from './CombatResolver';
import { RatingService } from '@services/RatingService';

const DEFAULT_CONFIG: GameConfig = {
  boardSize: 5,
  warriorsPerPlayer: 3,
};

export class GameEngine {
  private state: GameState;
  private observers: Set<GameEventObserver> = new Set();
  private config: GameConfig;

  constructor(config: Partial<GameConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.state = this.createInitialState();
  }

  /**
   * Get current game state
   */
  getState(): Readonly<GameState> {
    return this.state;
  }

  /**
   * Subscribe to game events
   */
  subscribe(observer: GameEventObserver): () => void {
    this.observers.add(observer);
    return () => this.observers.delete(observer);
  }

  /**
   * Emit a game event to all observers
   */
  private emit(type: GameEvent['type'], payload: unknown): void {
    const event: GameEvent = {
      type,
      payload,
      timestamp: Date.now(),
    };
    this.observers.forEach(observer => observer.onGameEvent(event));
  }

  /**
   * Create initial game state
   */
  private createInitialState(): GameState {
    const ratings = RatingService.loadRatings();
    return {
      phase: 'title',
      players: {
        player1: {
          id: 'player1',
          name: '',
          warriors: [],
          startingRow: 4,
          rating: ratings.player1,
        },
        player2: {
          id: 'player2',
          name: '',
          warriors: [],
          startingRow: 0,
          rating: ratings.player2,
        },
      },
      board: BoardManager.createEmptyBoard(),
      currentTurn: 'player1',
      startingPlayer: 'player1',
      turnNumber: 1,
      selectedWarrior: null,
      actionHistory: [],
      winner: null,
      availableWarriors: [],
      gameMode: '2-player',
      aiConfig: null,
      ratingChanges: null,
    };
  }

  /**
   * Load available warriors from definitions
   */
  loadWarriors(warriors: readonly WarriorDefinition[]): void {
    this.state = {
      ...this.state,
      availableWarriors: warriors,
    };
  }

  /**
   * Transition to a new phase
   */
  transitionTo(phase: GamePhase): void {
    const previousPhase = this.state.phase;
    this.state = { ...this.state, phase };
    this.emit('phase-changed', { from: previousPhase, to: phase });
  }

  /**
   * Start a new game with the specified mode
   * - 1-player: Goes to warrior selection
   * - 2-player: Goes to warrior selection
   * - online: Goes to room scene
   */
  startGame(mode: '1-player' | '2-player' | 'online'): void {
    this.state = {
      ...this.state,
      gameMode: mode,
      aiConfig: null,
    };

    if (mode === 'online') {
      this.transitionTo('room');
    } else {
      this.transitionTo('warrior-selection');
    }
  }

  /**
   * Proceed to warrior selection (used after room scene in online mode)
   */
  proceedToWarriorSelection(): void {
    this.transitionTo('warrior-selection');
  }

  /**
   * Proceed to AI difficulty selection (after P1 selects warriors in 1-player mode)
   */
  proceedToAIDifficulty(): void {
    this.transitionTo('ai-difficulty');
  }

  /**
   * Proceed to room scene (for online mode)
   */
  proceedToRoom(): void {
    this.transitionTo('room');
  }

  /**
   * Set game mode (called when player 2 chooses how to play)
   */
  setGameMode(mode: '1-player' | '2-player' | 'online', aiDifficulty?: AIDifficulty): void {
    if (mode === '1-player' && aiDifficulty) {
      this.state = {
        ...this.state,
        gameMode: mode,
        aiConfig: {
          difficulty: aiDifficulty,
          thinkingDelayMs: this.getThinkingDelay(aiDifficulty),
        },
      };
    } else {
      this.state = {
        ...this.state,
        gameMode: mode,
        aiConfig: null,
      };
    }
  }


  /**
   * Set the starting player (used for online mode when host broadcasts coin flip result)
   */
  setStartingPlayer(startingPlayer: PlayerId): void {
    this.state = {
      ...this.state,
      currentTurn: startingPlayer,
      startingPlayer: startingPlayer,
      turnNumber: 1,
    };
  }

  /**
   * Get thinking delay based on AI difficulty
   */
  private getThinkingDelay(difficulty: AIDifficulty): number {
    switch (difficulty) {
      case 'easy':
        return 1500;
      case 'medium':
        return 1000;
      case 'hard':
        return 500;
    }
  }

  /**
   * Check if current turn is AI-controlled
   */
  isAITurn(): boolean {
    return (
      this.state.gameMode === '1-player' &&
      this.state.currentTurn === 'player2' &&
      this.state.phase === 'battle' &&
      this.state.winner === null
    );
  }

  /**
   * Set player names (legacy - use confirmTeamSelection instead)
   */
  setPlayerNames(player1Name: string, player2Name: string): void {
    this.state = {
      ...this.state,
      players: {
        player1: { ...this.state.players.player1, name: player1Name },
        player2: { ...this.state.players.player2, name: player2Name },
      },
    };
  }

  /**
   * Confirm team selection with all player data (new unified flow)
   */
  confirmTeamSelection(
    player1Name: string,
    player1WarriorIds: string[],
    player1Rating: number,
    player2Name: string,
    player2WarriorIds: string[],
    player2Rating: number,
    aiDifficulty: AIDifficulty | null
  ): void {
    // Set up AI config if in 1-player mode
    if (this.state.gameMode === '1-player' && aiDifficulty) {
      const aiRating = RatingService.getAIRating(aiDifficulty);
      this.state = {
        ...this.state,
        aiConfig: {
          difficulty: aiDifficulty,
          thinkingDelayMs: this.getThinkingDelay(aiDifficulty),
        },
        players: {
          ...this.state.players,
          player2: {
            ...this.state.players.player2,
            rating: aiRating,
          },
        },
      };
    }

    // Set player names and ratings
    this.state = {
      ...this.state,
      players: {
        player1: {
          ...this.state.players.player1,
          name: player1Name,
          rating: player1Rating,
        },
        player2: {
          ...this.state.players.player2,
          name: player2Name,
          rating: this.state.gameMode === '1-player'
            ? this.state.players.player2.rating // Keep AI rating
            : player2Rating,
        },
      },
    };

    // Select warriors for both players
    this.selectWarriors('player1', player1WarriorIds);
    this.selectWarriors('player2', player2WarriorIds);

    // Note: Don't transition here - TeamSelectionScene will show coin flip animation
    // and then call performCoinFlipAndStartBattle()
  }

  /**
   * Select warriors for a player
   */
  selectWarriors(playerId: PlayerId, warriorIds: string[]): void {
    if (warriorIds.length !== this.config.warriorsPerPlayer) {
      throw new Error(`Must select exactly ${this.config.warriorsPerPlayer} warriors`);
    }

    const warriors: WarriorInstance[] = warriorIds.map((id, index) => {
      const definition = this.state.availableWarriors.find(w => w.id === id);
      if (!definition) {
        throw new Error(`Warrior not found: ${id}`);
      }

      return {
        instanceId: `${playerId}-${id}-${index}`,
        definitionId: id,
        ownerId: playerId,
        currentHealth: definition.stats.health,
        position: null,
        attackCooldown: 0, // Start at 0, needs to charge
        healCooldown: 0, // Start at 0, needs to charge
        dashCooldown: 0, // Start at 0, needs to charge
        definition,
      };
    });

    this.state = {
      ...this.state,
      players: {
        ...this.state.players,
        [playerId]: {
          ...this.state.players[playerId],
          warriors,
        },
      },
    };
  }

  /**
   * Confirm warrior selection (legacy method - kept for compatibility)
   * Note: With the new flow, use confirmTeamSelection instead
   */
  confirmWarriorSelection(): void {
    const p1Count = this.state.players.player1.warriors.length;
    const p2Count = this.state.players.player2.warriors.length;

    if (p1Count !== this.config.warriorsPerPlayer || p2Count !== this.config.warriorsPerPlayer) {
      throw new Error('Both players must select their warriors first');
    }

    // No longer transitions - caller should show coin flip animation
    // and then call performCoinFlipAndStartBattle()
  }

  /**
   * Perform coin flip and determine first player (does NOT transition to battle)
   */
  performCoinFlip(): PlayerId {
    const firstPlayer: PlayerId = Math.random() < 0.5 ? 'player1' : 'player2';
    this.state = {
      ...this.state,
      currentTurn: firstPlayer,
      startingPlayer: firstPlayer,
      turnNumber: 1, // Reset turn number for new battle
    };
    return firstPlayer;
  }

  /**
   * Perform coin flip and immediately start battle
   * Used when coin flip animation is shown in TeamSelectionScene
   */
  performCoinFlipAndStartBattle(): PlayerId {
    const firstPlayer = this.performCoinFlip();
    this.startBattle();
    return firstPlayer;
  }

  /**
   * Transition to coin flip phase
   */
  proceedToCoinFlip(): void {
    this.transitionTo('coin-flip');
  }

  /**
   * Start the battle phase (call after coin flip result is shown)
   */
  startBattle(): void {
    // Place warriors on the board
    this.placeWarriors();
    this.transitionTo('battle');
  }

  /**
   * Place warriors in their starting positions
   */
  private placeWarriors(): void {
    let board = BoardManager.createEmptyBoard();
    const allWarriors: WarriorInstance[] = [];

    // Place player 1 warriors (bottom row, columns 1, 2, 3)
    const p1Warriors = this.state.players.player1.warriors.map((warrior, index) => {
      const position: BoardPosition = { row: 4, col: index + 1 };
      board = BoardManager.placeWarrior(board, warrior.instanceId, position);
      return { ...warrior, position };
    });

    // Place player 2 warriors (top row, columns 1, 2, 3)
    const p2Warriors = this.state.players.player2.warriors.map((warrior, index) => {
      const position: BoardPosition = { row: 0, col: index + 1 };
      board = BoardManager.placeWarrior(board, warrior.instanceId, position);
      return { ...warrior, position };
    });

    allWarriors.push(...p1Warriors, ...p2Warriors);

    this.state = {
      ...this.state,
      board,
      players: {
        player1: { ...this.state.players.player1, warriors: p1Warriors },
        player2: { ...this.state.players.player2, warriors: p2Warriors },
      },
    };
  }

  /**
   * Get all warriors in the game
   */
  getAllWarriors(): WarriorInstance[] {
    return [
      ...this.state.players.player1.warriors,
      ...this.state.players.player2.warriors,
    ];
  }

  /**
   * Select a warrior to control
   */
  selectWarrior(warriorInstanceId: string): void {
    const warrior = this.getAllWarriors().find(w => w.instanceId === warriorInstanceId);
    if (!warrior) {
      throw new Error('Warrior not found');
    }

    if (warrior.ownerId !== this.state.currentTurn) {
      throw new Error('Cannot select enemy warrior');
    }

    if (!warrior.position) {
      throw new Error('Warrior is defeated');
    }

    this.state = {
      ...this.state,
      selectedWarrior: warriorInstanceId,
    };
    this.emit('warrior-selected', { warrior: warriorInstanceId });
  }

  /**
   * Deselect current warrior
   */
  deselectWarrior(): void {
    this.state = {
      ...this.state,
      selectedWarrior: null,
    };
    this.emit('warrior-deselected', {});
  }

  /**
   * Execute a move action
   */
  move(to: BoardPosition): void {
    if (!this.state.selectedWarrior) {
      throw new Error('No warrior selected');
    }

    const warrior = this.getAllWarriors().find(
      w => w.instanceId === this.state.selectedWarrior
    );
    if (!warrior) {
      throw new Error('Warrior not found');
    }

    const result = TurnManager.executeMove(
      warrior,
      to,
      this.state.board,
      this.getAllWarriors()
    );

    if (!result.success) {
      throw new Error(result.error);
    }

    this.applyTurnResult(result);
    this.emit('warrior-moved', { warrior: warrior.instanceId, to });
    this.endTurn();
  }

  /**
   * Execute a dash action
   */
  dash(to: BoardPosition): void {
    if (!this.state.selectedWarrior) {
      throw new Error('No warrior selected');
    }

    const warrior = this.getAllWarriors().find(
      w => w.instanceId === this.state.selectedWarrior
    );
    if (!warrior) {
      throw new Error('Warrior not found');
    }

    const result = TurnManager.executeDash(
      warrior,
      to,
      this.state.board,
      this.getAllWarriors()
    );

    if (!result.success) {
      throw new Error(result.error);
    }

    this.applyTurnResult(result);
    this.emit('warrior-dashed', { warrior: warrior.instanceId, to });
    this.endTurn();
  }

  /**
   * Execute an attack action
   */
  attack(): void {
    if (!this.state.selectedWarrior) {
      throw new Error('No warrior selected');
    }

    const warrior = this.getAllWarriors().find(
      w => w.instanceId === this.state.selectedWarrior
    );
    if (!warrior) {
      throw new Error('Warrior not found');
    }

    // Save positions before attack to know where defeated warriors were
    const allWarriors = this.getAllWarriors();
    const positionsBeforeAttack = new Map<string, import('@/types').BoardPosition>();
    for (const w of allWarriors) {
      if (w.position) {
        positionsBeforeAttack.set(w.instanceId, { ...w.position });
      }
    }

    const result = TurnManager.executeAttack(
      warrior,
      this.state.board,
      allWarriors
    );

    if (!result.success) {
      throw new Error(result.error);
    }

    this.applyTurnResult(result);
    this.emit('warrior-attacked', { warrior: warrior.instanceId, position: warrior.position });

    // Emit damage events for warriors that survived
    if (result.damagedWarriors) {
      for (const damageInfo of result.damagedWarriors) {
        this.emit('warrior-damaged', {
          warrior: damageInfo.targetId,
          damage: damageInfo.damage,
          position: damageInfo.position,
        });
      }
    }

    if (result.defeatedWarriors) {
      for (const defeatedId of result.defeatedWarriors) {
        const position = positionsBeforeAttack.get(defeatedId);
        this.emit('warrior-defeated', { warrior: defeatedId, position });
      }
    }

    this.checkGameEnd();
    if (this.state.phase !== 'game-over') {
      this.endTurn();
    }
  }

  /**
   * Execute a heal action
   */
  heal(): void {
    if (!this.state.selectedWarrior) {
      throw new Error('No warrior selected');
    }

    const warrior = this.getAllWarriors().find(
      w => w.instanceId === this.state.selectedWarrior
    );
    if (!warrior) {
      throw new Error('Warrior not found');
    }

    const result = TurnManager.executeHeal(
      warrior,
      this.getAllWarriors(),
      this.state.board
    );

    if (!result.success) {
      throw new Error(result.error);
    }

    this.applyTurnResult(result);

    // Extract healedWarriors from the action
    const healAction = result.action as import('@/types').HealAction;
    const healedWarriors = healAction?.healedWarriors || {};

    this.emit('warrior-healed', {
      warrior: warrior.instanceId,
      healedWarriors
    });
    this.endTurn();
  }

  /**
   * Apply turn result to state
   */
  private applyTurnResult(result: import('./TurnManager').TurnResult): void {
    const p1Warriors = result.updatedWarriors.filter(w => w.ownerId === 'player1');
    const p2Warriors = result.updatedWarriors.filter(w => w.ownerId === 'player2');

    this.state = {
      ...this.state,
      board: result.newBoard,
      players: {
        player1: { ...this.state.players.player1, warriors: p1Warriors },
        player2: { ...this.state.players.player2, warriors: p2Warriors },
      },
      actionHistory: result.action
        ? [...this.state.actionHistory, result.action]
        : this.state.actionHistory,
    };
  }

  /**
   * End current turn and switch to next player
   * Turn number only increments after BOTH players have moved (when switching back to starting player)
   */
  private endTurn(): void {
    const nextPlayer = TurnManager.getNextPlayer(this.state.currentTurn);
    // Increment turn number only when switching back to the starting player
    // (i.e., after both players have completed their moves for this turn)
    const shouldIncrementTurn = nextPlayer === this.state.startingPlayer;

    this.state = {
      ...this.state,
      currentTurn: nextPlayer,
      turnNumber: shouldIncrementTurn ? this.state.turnNumber + 1 : this.state.turnNumber,
      selectedWarrior: null,
    };
    this.emit('turn-changed', { player: nextPlayer, turn: this.state.turnNumber });

    // Check for tie condition (100 turns)
    this.checkGameEnd();
  }

  /**
   * Check if the game has ended
   */
  private checkGameEnd(): void {
    const p1Defeated = CombatResolver.isPlayerDefeated('player1', this.getAllWarriors());
    const p2Defeated = CombatResolver.isPlayerDefeated('player2', this.getAllWarriors());
    const isTie = this.state.turnNumber >= 100;

    if (p1Defeated || p2Defeated) {
      const winner: PlayerId = p1Defeated ? 'player2' : 'player1';
      const loser: PlayerId = winner === 'player1' ? 'player2' : 'player1';

      // Calculate rating changes
      const winnerRating = this.state.players[winner].rating;
      const loserRating = this.state.players[loser].rating;
      const ratingChange = RatingService.calculateEloChange(winnerRating, loserRating);

      // Apply rating changes
      const newRatings = RatingService.applyRatingChange(
        {
          player1: this.state.players.player1.rating,
          player2: this.state.players.player2.rating,
        },
        winner,
        ratingChange
      );

      // Save to localStorage (only player1 rating in 1-player mode, both in 2-player)
      if (this.state.gameMode === '1-player') {
        // Only persist human player's rating
        const currentRatings = RatingService.loadRatings();
        RatingService.saveRatings({
          player1: newRatings.player1,
          player2: currentRatings.player2, // Keep existing player2 rating for 2-player games
        });
      } else {
        RatingService.saveRatings(newRatings);
      }

      // Calculate deltas for display
      const ratingChanges = {
        player1: newRatings.player1 - this.state.players.player1.rating,
        player2: newRatings.player2 - this.state.players.player2.rating,
      };

      this.state = {
        ...this.state,
        winner,
        phase: 'game-over',
        players: {
          player1: { ...this.state.players.player1, rating: newRatings.player1 },
          player2: { ...this.state.players.player2, rating: newRatings.player2 },
        },
        ratingChanges,
      };
      this.emit('game-ended', { winner, ratingChanges });
    } else if (isTie) {
      // Tie - calculate rating changes based on ELO tie formula
      const tieChange = RatingService.calculateTieChange(
        this.state.players.player1.rating,
        this.state.players.player2.rating
      );

      const newRatings = {
        player1: Math.max(100, this.state.players.player1.rating + tieChange.player1Delta),
        player2: Math.max(100, this.state.players.player2.rating + tieChange.player2Delta),
      };

      // Save to localStorage (only player1 rating in 1-player mode, both in 2-player)
      if (this.state.gameMode === '1-player') {
        const currentRatings = RatingService.loadRatings();
        RatingService.saveRatings({
          player1: newRatings.player1,
          player2: currentRatings.player2,
        });
      } else {
        RatingService.saveRatings(newRatings);
      }

      const ratingChanges = {
        player1: tieChange.player1Delta,
        player2: tieChange.player2Delta,
      };

      this.state = {
        ...this.state,
        winner: 'tie',
        phase: 'game-over',
        players: {
          player1: { ...this.state.players.player1, rating: newRatings.player1 },
          player2: { ...this.state.players.player2, rating: newRatings.player2 },
        },
        ratingChanges,
      };
      this.emit('game-ended', { winner: 'tie', ratingChanges });
    }
  }

  /**
   * Reset game to initial state (back to title screen)
   */
  reset(): void {
    // Clear stored ratings when starting over
    RatingService.resetRatings();
    const availableWarriors = this.state.availableWarriors;
    this.state = this.createInitialState();
    this.state = { ...this.state, availableWarriors };
    this.emit('game-reset', {});
  }

  /**
   * Play again with same players and warriors (rematch)
   * Resets warriors and prepares for a new battle without going to team selection
   * Call performCoinFlip() and then startBattle() after showing coin flip animation
   */
  playAgain(): void {
    // Reset warriors to full health and clear positions
    const resetWarriors = (warriors: WarriorInstance[]): WarriorInstance[] => {
      return warriors.map(w => ({
        ...w,
        currentHealth: w.definition.stats.health,
        position: null,
        attackCooldown: 0,
        dashCooldown: 0,
      }));
    };

    const p1Warriors = resetWarriors(this.state.players.player1.warriors);
    const p2Warriors = resetWarriors(this.state.players.player2.warriors);

    this.state = {
      ...this.state,
      // Stay in game-over phase - UI will show coin flip and then transition to battle
      board: BoardManager.createEmptyBoard(),
      currentTurn: 'player1',
      turnNumber: 1,
      selectedWarrior: null,
      actionHistory: [],
      winner: null,
      ratingChanges: null, // Clear rating changes for next game
      players: {
        player1: { ...this.state.players.player1, warriors: p1Warriors },
        player2: { ...this.state.players.player2, warriors: p2Warriors },
      },
    };
    // Don't emit game-reset - the UI will handle transitioning after coin flip
  }
}
